import React from 'react'
import { TwitterTweetEmbed } from 'react-twitter-embed'
import { Link } from '@ta-interaktiv/contentbricks'

export const TEXT_FR = {
  title: <>Chez Credit Suisse, deux décennies de promesses non tenues</>,
  lead: (
    <>
      Chaque fois que la banque s’est retrouvée au cœur d’un scandale, elle a
      promis de s’améliorer. Mais c’est l’inverse qui s’est produit. Nous avons
      comparé dix promesses à la réalité.
    </>
  ),
  intro: (
    <>
      <p>
        Depuis dimanche soir, Credit Suisse fait les gros titres de la presse
        internationale. La fuite de données «Suisse Secrets» a exposé le casting
        douteux de certains clients de la grande banque - chefs d’États
        autoritaires, espions ou criminels. Le collectif de journalistes OCCRP a
        identifié plus de{' '}
        <Link href='https://cdn.occrp.org/projects/suisse-secrets-interactive/en/?_gl=1%2a4cjwi2%2a_ga%2aMTAyNjI3MDcyNi4xNjQ1Mzc3MjQx%2a_ga_NHCZV5EYYY%2aMTY0NTM3NzI0MC4xLjEuMTY0NTM3OTU5Mi42MA'>
          60 clients problématiques ou criminels
        </Link>{' '}
        qui possédaient jusqu’à 8 milliards de francs chez Credit Suisse -
        certains jusqu’au milieu des années 2010.
      </p>
      <p>
        La banque a réagi à ces révélations en rejetant «fermement les
        allégations et insinuations concernant les prétendues pratiques
        commerciales de la banque». Credit Suisse a fait des «investissements
        supplémentaires considérables dans la lutte contre la criminalité
        financière» et «notre stratégie place la gestion des risques au cœur de
        nos activités», ajoute le communiqué.
      </p>
      <p>
        Depuis vingt ans, la banque réagit à chaque scandale en affirmant qu’il
        s’agit de reliques du passé et que la banque est devenue irréprochable.
        Que valent ces promesses? La cellule enquête de Tamedia s'est penchée
        sur 10 promesses faites par la direction de Credit Suisse, dont voici la
        chronologie:
      </p>
    </>
  ),
  updated: <>Mis à jour le 22 février, 2021</>,
  credits: ['Analyse des données et texte', 'Visualisation', 'Collaboration'],
  promises: [
    {
      idx: 2000,
      year: '2000',
      promise: (
        <>
          <p>
            Au début des années 2000, Credit Suisse est cloué au pilori sur la
            scène internationale. La banque est forcée d’admettre que la famille
            de dictateurs nigérians Abacha a placé plus de 200 millions de
            francs suisses chez elle. De l’argent volé par un despote pour
            atterrir sur des comptes suisses: on nage en plein cliché.
          </p>

          <p className='indent'>
            «Je voyage à travers le monde pour dire que la place financière
            suisse est propre, et voilà ce qui se produit», s’emporte alors le
            ministre des Finances Kaspar Villiger. Lors de son enquête, la
            Commission fédérale des banques fait état «de manquements parfois
            graves» ainsi que «de fautes individuelles grossières».
          </p>

          <p className='indent'>
            En réaction, Credit Suisse fait sa première promesse, en 2001.
          </p>
        </>
      )
    },
    {
      idx: 2001,
      imageId: 1,
      year: <>2001: «Correct, juste et éthique»</>,
      name: <>Lukas Mühlemann</>,
      role: <>Directeur de Credit Suisse</>,
      promise: (
        <>
          <p>
            «Tous nos employés reçoivent un code de conduite. Il stipule qu’ils
            sont tenus de se comporter de manière correcte, équitable et éthique
            […]. Nous sommes intransigeants à ce sujet. Tous savent qu’ils
            seront licenciés si un épisode similaire aux fonds d’Abacha se
            reproduit.»
          </p>
        </>
      ),
      source: <>«Magazin», le 26 janvier 2001</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            Alors que l’entreprise est encore dirigée par Lukas Mühlemann,
            l’énorme affaire de corruption touchant la compagnie pétrolière
            française <b>Elf-Aquitaine</b> révèle que 245 millions de francs
            suisses se trouvent chez Credit Suisse. Aux États-Unis, la banque
            est impliquée dans la manipulation du bilan du géant de l'énergie{' '}
            <b>Enron</b> . La participation de Credit Suisse dans une banque
            douteuse en Argentine est également révélée, mobilisant à nouveau la
            Commission fédérale des banques.
          </p>
        </>
      )
    },
    {
      idx: 2003,
      imageId: 2,
      year: <>2003: «La confiance est notre priorité»</>,
      name: <>Walter Kielholz</>,
      role: <>Président de Credit Suisse</>,
      promise: (
        <>
          <p>
            «Restaurer la confiance dans l’entreprise est peut-être notre plus
            grande priorité stratégique.» Le but sera atteint si «personne ne
            doute de l’efficacité de notre gestion des risques » et si
            l’autorité de surveillance «parle de nous avec respect».
          </p>
        </>
      ),
      source: <>Assemblée générale de Credit Suisse, 25 avril 2003</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            En 2004, 60 millions de francs suisses d'origine douteuse
            apparaissent à nouveau dans les comptes de Credit Suisse – ils
            appartiennent cette fois à la <b>mafia japonaise</b>. Dans un même
            temps, la banque enfreint allègrement les <b>sanctions</b>: jusqu'en
            2006, Credit Suisse traite des paiements à hauteur de 1,6 milliard
            pour des pays et des individus figurant sur la liste des sanctions
            américaines. Le montant des paiements venus d’Iran est
            particulièrement important.
          </p>

          <p className='indent'>
            L’une des pratiques des employés de Credit suisse consiste à
            remplacer le nom de la banque d’origine dans le système SWIFT par
            une description vague: «Commande d'un client». Credit Suisse
            dissimule ainsi la véritable nature des transactions.
          </p>

          <p className='indent'>
            Credit Suisse passe plus tard un accord avec les États-Unis et{' '}
            <b>verse 536 millions de dollars</b> d’amendes diverses pour ses
            pratiques. «Le comportement criminel de Credit Suisse dans cette
            affaire est tout simplement stupéfiant, tant par son ampleur que par
            sa complexité», déclare alors le procureur général américain Eric
            Holder. L’Autorité de surveillance des banques, la FINMA, réprimande
            sévèrement la banque.
          </p>
        </>
      )
    },
    {
      idx: 2007,
      imageId: 3,
      year: <>2007: «Les plus hauts standards internationaux»</>,
      name: <>Brady Dougan et Walter Kielholz</>,
      role: <>Directeur général et président de Credit Suisse</>,
      promise: (
        <>
          <p>
            «Nous nous efforçons de respecter systématiquement les normes
            internationales les plus strictes en matière d'éthique, d’intégrité
            et de responsabilité sociale et environnementale, et d’assurer leur
            mise en œuvre grâce à des processus d’audit interne complets."
          </p>
        </>
      ),
      source: <>Corporate Citizenship Report 2007</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            De 2004 à 2007, des membres d’un gang de{' '}
            <b>trafiquants de drogue bulgare</b> déposent des valises remplies
            d’argent liquide au siège de Credit Suisse, pour un total de 54
            millions de francs suisses. La banque est actuellement jugée par le
            Tribunal pénal fédéral pour cette affaire. Le Ministère public de la
            Confédération (MPC) accuse Credit Suisse de blanchiment d’argent à
            grande échelle.
          </p>

          <p className='indent'>
            Les procureurs fédéraux estiment que Credit suisse a{' '}
            <b>failli à tous les niveaux</b>. Parmi les nombreuses lacunes
            dénoncées, un encadrement insuffisant des conseillers à la clientèle
            non expérimentés, des systèmes informatiques défaillants, une
            clarification et une documentation insuffisantes des opérations à
            risque et des profils des clients, ou encore une absence de vue
            d'ensemble sur l'activité des clients.
          </p>

          <p className='indent'>
            Au début de l’année 2008, Credit Suisse a également accepté des
            millions dans le cadre de ce qui s’est révélé l’une des plus
            importantes affaires de crime économique de Russie, dite{' '}
            <b>l’affaire Magnitski</b>. S’ensuivront des années d’enquête.
          </p>

          <p className='indent'>
            En parallèle, la banque participe à grande échelle à l’octroi de
            créances hypothécaires américaines pourries (<b>subprimes</b>), à
            l’origine de la crise financière mondiale de 2008. Elle est
            condamnée à payer aux autorités américaines un total de 5,28
            milliards de dollars d'amendes et de compensations
          </p>
        </>
      )
    },
    {
      idx: 2008,
      imageId: 2,
      year: <>2008: «Dans le cadre strict de la loi»</>,
      name: <>Walter Kielholz</>,
      role: <>Président de Credit suisse</>,
      promise: (
        <>
          <p>
            «Nous avons exercé nos activités transfrontalières américaines
            uniquement dans le cadre strict des exigences légales. [...] Nous
            n’avons connaissance d’aucune enquête du fisc américain à notre
            encontre.»
          </p>
        </>
      ),
      source: <>«Finanz und Wirtschaft», 6 décembre 2008</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            La banque contribue en réalité à <b>l’évasion fiscale</b> de
            nombreux citoyens américains jusqu'en 2009. Credit Suisse paie
            ensuite un montant de 2,8 milliards de dollars d’amende aux
            États-Unis. À titre comparatif, UBS, qui a aussi contribué aux
            délits fiscaux de l’époque, n’a dû payer «que» 780 millions. Dans sa
            déclaration de culpabilité, Credit Suisse reconnaît avoir opéré
            illégalement pendant des décennies et avoir «sciemment et
            volontairement» aidé des milliers de clients américains à cacher
            leurs avoirs au fisc.
          </p>
          <p className='indent'>
            La FINMA réprimande également la banque «pour violation grave des
            exigences en matière de garantie et d’organisation» dans le cadre de
            ses activités aux États-Unis.
          </p>
          <p className='indent'>
            Dans une autre procédure qui n'a pas encore été rendue publique,
            l’autorité de surveillance suisse est parvenue à la conclusion que
            la banque avait{' '}
            <b>violé à plusieurs reprises son devoir de diligence</b> dans la
            lutte contre le blanchiment d’argent de 2005 à 2010. Dans ce cas, la
            banque n’aurait pas sérieusement éclairci l’origine des avoirs de
            clients russes.
          </p>
        </>
      )
    },
    {
      idx: 2011,
      imageId: 4,
      year: <>2011: «Un service antiblanchiment de première classe»</>,
      name: <>Walter Berchtold</>,
      role: <>Chef du Private Banking</>,
      promise: (
        <>
          <p>
            «Certaines problématiques exigent un service de lutte contre le
            blanchiment de premier ordre. Le strict respect de l’ensemble des
            lois applicables est absolument fondamental pour Credit Suisse.»
          </p>
        </>
      ),
      source: <>«Finanz und Wirtschaft», 28 mai 2011</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            À Hong Kong, la banque octroie des emplois aux membres de la famille
            et aux amis de hauts fonctionnaires chinois pour conclure de
            lucratifs investissements bancaires. Épinglé pour{' '}
            <b>népotisme et corruption</b> aux États-Unis, Credit Suisse verse
            plus tard 77 millions de dollars aux autorités américaines. Une
            filiale américaine de la banque est également condamnée à une amende
            de 16,5 millions de dollars pour avoir enfreint les réglementations
            antiblanchiment d’argent.
          </p>
          <p className='indent'>
            En Suisse aussi, Credit Suisse traverse de nouvelles turbulences,
            cette fois au sein de sa succursale genevoise, où un{' '}
            <b>gestionnaire star de la banque détourne plus de 120 millions</b>{' '}
            appartenant à l’un des plus gros clients de Credit Suisse. Le
            banquier est condamné à 5 ans de prison. Il se suicidera deux ans
            plus tard. À nouveau, la FINMA constate des lacunes flagrantes. Bien
            que le banquier vedette ait enfreint la réglementation pendant des
            années, Credit Suisse ne l’a jamais sanctionné. Au contraire: la
            banque l’a récompensé par des bonus de plusieurs millions.
          </p>
        </>
      )
    },
    {
      idx: 2013,
      imageId: 'logo',
      year: <>2013: «Engagé pour les plus démunis»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          <p>
            «Nous nous engageons à améliorer les conditions de vie des personnes
            défavorisées.»
          </p>
        </>
      ),
      source: <>Rapport sur la responsabilité d’entreprise 2013</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            Au lieu d’aider les habitants des pays en développement, Credit
            Suisse contribue à la <b>faillite d’un pays africain</b> tout
            entier: le Mozambique. En 2013, la banque accorde à deux entreprises
            publiques mozambicaines un prêt d’un milliard par l’intermédiaire de
            sa succursale de Londres. Une grande partie de cet argent a été
            détournée par des fonctionnaires corrompus, rendant le pays
            insolvable - avec des conséquences dramatiques pour une population
            très pauvre.
          </p>
          <p className='indent'>
            Credit Suisse a ensuite dû payer aux États-Unis une amende d'environ
            un demi-milliard de dollars. Dans sa propre enquête, la FINMA est
            parvenue à la conclusion que la gestion des risques de la banque
            «comportait de <b>graves lacunes</b>» et que la banque avait violé
            son obligation de déclarer des soupçons de blanchiment d’argent.
          </p>
          <p className='indent'>
            La liste ne s’arrête pas là. Lorsqu’en 2015 des procureurs suisses
            et américains s’intéressent à des dirigeants de football corrompus,
            ils découvrent des millions blanchis sur des comptes de Credit
            Suisse. <b>La FINMA lance trois enquêtes</b>: une sur les fonds de
            la FIFA, une sur des fonds liés à une affaire de corruption de
            plusieurs milliards de dollars impliquant la compagnie pétrolière
            brésilienne Petrobras, et une sur les fonds corrompus d’un Venezuela
            en crise.
          </p>
          <p className='indent'>
            Dans les trois affaires, la FINMA est parvenue à la conclusion que
            Credit Suisse avait violé des «obligations de surveillance
            élémentaires en matière de lutte contre le blanchiment d’argent»,
            «au fil des ans et à plusieurs reprises».
          </p>
          <p className='indent'>
            «Les comportements identifiés ne sont pas des cas isolés et ne
            concernent pas des unités organisationnelles isolées ou des segments
            de clientèle (spécifiques à un pays) de Credit Suisse, écrit la
            FINMA. Tous les rapports d'audit - FIFA, PDVSA, Petrobras - arrivent
            à une conclusion similaire dans tous les domaines et montrent le
            même schéma de manquements à la diligence due en matière de
            blanchiment d’argent.{' '}
            <b>
              Le non-respect des exigences en matière de diligence raisonnable
              au sein de la banque est donc un problème général
            </b>
            , qui a un impact significatif sur l’ensemble de son dispositif de
            lutte contre le blanchiment d’argent.»
          </p>
        </>
      )
    },
    {
      idx: 2016,
      imageId: 'logo',
      year: <>2016: «La responsabilité dans notre ADN»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          <p>
            «La responsabilité sociale et entrepreneuriale fait partie de notre
            ADN.»
          </p>
        </>
      ),
      source: <>Annonce publicitaire, 21 novembre 2016</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            Sous le nouveau directeur général Tidjane Thiam, Credit Suisse fait{' '}
            <b>surveiller des employés par des détectives privés</b>. La banque
            commande les filatures de sept personnes entre 2016 et 2019. Les
            détectives suivent notamment le patron de la gestion de fortune de
            Credit Suisse et photographient sa femme. Même l’ex-mari de la
            petite amie de Tidjane Thiam, qui ne travaille pas à la banque, est
            visé.
          </p>
          <p className='indent'>
            La FINMA intervient à nouveau, constatant une fois encore des
            «défaillances importantes» dans la gouvernance d’entreprise de
            Credit Suisse. Les décisions concernant les filatures ont été prises
            de manière informelle et sans aucune justification compréhensible,
            et ce même si elles comportaient de grands risques de réputation. La
            direction a caché ces agissements, notamment en modifiant une
            facture. Le tout témoigne d'une «
            <b>
              culture d'entreprise inappropriée d’une partie de la direction
            </b>{' '}
            opérationnelle de Credit Suisse de l’époque», considère la FINMA.
          </p>
          <p className='indent'>
            Entre-temps, le détournement présumé de dons de plusieurs millions
            de dollars s’organise au <b>Vatican</b>. Des sommes importantes
            finissent sur des comptes suisses de la banque. À Londres, Paris et
            Amsterdam, les autorités mènent une{' '}
            <b>perquisition à grande échelle</b> dans les succursales de Credit
            Suisse, pour soupçons de fraude fiscale et de blanchiment d’argent.
            La presse fait état de 55’000 comptes suspects.
          </p>
          <p className='indent'>
            En décembre 2020, le Ministère public de la Confédération porte
            plainte dans l’affaire du gang de trafiquants bulgares, dont les
            membres sont venus jusqu’à la Paradeplatz pour déposer leurs valises
            remplies d’argent. Presque au même moment, la Réserve fédérale
            américaine annonce qu’elle a trouvé des{' '}
            <b>
              lacunes dans le système de lutte contre le blanchiment d’argent
            </b>{' '}
            de la banque aux États-Unis. Elle lui donne 90 jours pour y
            remédier.
          </p>
        </>
      )
    },
    {
      idx: 2020,
      imageId: 'logo',
      year: <>2020: «Respect de toutes les exigences»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          <p>
            «La banque a massivement étendu et renforcé son système de défense
            interne contre le blanchiment d’argent depuis plus de douze ans et
            plus particulièrement ces dernières années [...]. Une croissance
            dans le respect de toutes les exigences légales et réglementaires
            constitue une priorité absolue pour le Credit Suisse.»
          </p>
        </>
      ),
      source: <>Communiqué de presse, 17 décembre 2020</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            Malgré des signes d’avertissement clairs, la banque spécule sur des
            transactions à haut risque alors que d’autres grandes banques se
            tiennent à l’écart. Lorsque éclatent les scandales{' '}
            <b>Greensill et Archegos</b>, Credit Suisse doit passer des
            milliards par pertes et profits. Le fonds Archegos lui fait{' '}
            <b>perdre à lui seul plus de cinq milliards</b>.
          </p>
          <p className='indent'>
            Credit Suisse commande sa propre enquête. Une fois de plus, la
            deuxième banque de Suisse n’a d’autre choix que d’admettre des
            lacunes massives dans le système de contrôle et de gestion des
            risques.
          </p>
          <p className='indent'>
            Après tous ces scandales, il est temps pour le président de longue
            date de Credit Suisse, Urs Rohner, de partir. Son successeur
            n’abandonne pas les promesses.
          </p>
        </>
      )
    },
    {
      idx: 2021,
      imageId: 5,
      year: <>2021: «Gestionnaire du risque dans l’âme» </>,
      name: <>António Horta-Osório</>,
      role: <>Président de Credit Suisse</>,
      promise: (
        <>
          <p>
            «J’ai insisté sur le fait que la gestion des risques est notre plus
            grande priorité. Chaque banquier doit être un gestionnaire de
            risques dans l’âme. Nous devons également développer une culture de
            la responsabilité personnelle.»
          </p>
        </>
      ),
      source: <>«NZZ», 1er juillet 2021</>,
      subheader: <>Ce qui s’est passé</>,
      reality: (
        <>
          <p>
            Fin 2021, Horta-Osório enfreint à plusieurs reprises les règles de
            quarantaine liées au coronavirus pour assister à des événements
            sportifs. Il démissionne dès janvier 2022.
          </p>

          <p className='indent'>
            Et maintenant, en réaction aux révélations «Suisse Secrets», Credit
            Suisse déclare:
          </p>
        </>
      )
    },
    {
      idx: 2022,
      imageId: 'logo',
      year: <>2022: «Respecter les normes les plus élevées»</>,
      name: <>Credit Suisse</>,
      promise: (
        <>
          {' '}
          <p>
            «En tant qu’institution financière mondiale de premier plan, le
            Credit Suisse est profondément conscient de sa responsabilité envers
            ses clients et le système financier dans son ensemble, qui est de
            veiller à ce que les normes de conduite les plus élevées soient
            respectées»
          </p>
        </>
      ),
      source: <>Communiqué, 20 février 2020</>,
      subheader: <>Et la suite?</>,
      reality: (
        <>
          <p>
            En vingt ans, Credit Suisse a payé plus de 10 milliards d’amendes
            diverses. Comment mettre fin à cette série noire? Il y a un mois, le
            célèbre dessinateur Chappatte publiait un dessin montrant des
            managers de Credit Suisse dans une salle de conférence. «Si on
            essayait une approche neuve et révolutionnaire?» propose le
            conférencier. Sur le tableau à côté de lui, il est écrit et souligné
            deux fois: «Les règles s’appliquent aussi à nous».
          </p>

          <TwitterTweetEmbed tweetId='1485275228351700994' />
        </>
      )
    }
  ]
}
